
import { useState, useEffect, useMemo } from "react";
import { DecimalEnum } from "@/types/fieldsOptions";
import { getDecimalEnums } from "@/services/decimalEnumService";
import { db } from "@/services/firebaseService";

// Cache storage with expiry
const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes in milliseconds
let cachedDecimalEnums: DecimalEnum[] | null = null;
let cacheTimestamp: number | null = null;

export const useDecimalEnums = () => {
  const [decimalEnums, setDecimalEnums] = useState<DecimalEnum[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchDecimalEnums = async () => {
      try {
        setIsLoading(true);
        
        // Check if we have valid cached data
        const now = Date.now();
        if (cachedDecimalEnums && cacheTimestamp && (now - cacheTimestamp < CACHE_EXPIRY)) {
          setDecimalEnums(cachedDecimalEnums);
          setIsLoading(false);
          return;
        }
        
        const fetchedEnums = await getDecimalEnums(db);
        
        // Update the cache
        cachedDecimalEnums = fetchedEnums;
        cacheTimestamp = now;
        
        setDecimalEnums(fetchedEnums);
      } catch (err) {
        console.error("Error fetching decimal enums:", err);
        setError(err instanceof Error ? err : new Error("Failed to fetch decimal enums"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDecimalEnums();
  }, []);

  // Create a lookup map for quick access by ID
  // Include key, value, longLabel, shortLabel, and altValue properties
  const enumsMap = useMemo(() => {
    const map = new Map<string, { key: string; value: number; longLabel?: string; shortLabel?: string; altValue?: number | null }>();
    decimalEnums.forEach(item => {
      map.set(item.id, { 
        key: item.id, 
        value: item.value,
        longLabel: item.longLabel,
        shortLabel: item.shortLabel,
        altValue: item.altValue
      });
    });
    return map;
  }, [decimalEnums]);

  // Function to get label by ID
  const getShortLabel = (id: string | undefined): string => {
    if (!id) return "—";
    const item = decimalEnums.find(item => item.id === id);
    return item ? item.shortLabel : id;
  };

  return {
    decimalEnums,
    isLoading,
    error,
    enumsMap,
    getShortLabel,
  };
};
